import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import authService from "../services/authService";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerifying(false);
        setError("No verification token provided");
        return;
      }
      
      try {
        const response = await authService.verifyEmail(token);
        setVerified(true);
        setAlreadyVerified(response.already_verified);
        if (!response.already_verified) {
          toast.success("Email verified successfully!");
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Verification failed");
      } finally {
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [token]);

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border bg-card">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border bg-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-heading">Verification Failed</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <Link to="/login" className="w-full">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Go to Login
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground text-center mt-2">
                Need a new verification link?{" "}
                <Link to="/resend-verification" className="text-primary hover:underline">
                  Resend verification email
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-emerald-500" />
          </div>
          <CardTitle className="text-2xl font-heading">
            {alreadyVerified ? "Email Already Verified" : "Email Verified!"}
          </CardTitle>
          <CardDescription>
            {alreadyVerified 
              ? "Your email address has already been verified. You can sign in to your account."
              : "Your email address has been verified. You can now sign in to your account."
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate("/login")}
            className="w-full bg-primary hover:bg-primary/90"
          >
            Sign In
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
